import { DateTime } from "luxon";
import type { LineageChange, NurseShiftLineageEntry, SyncChangeAction } from "./shiftLineage";

/**
 * Collapses the raw sync-decision feed into ONE ROW PER SHIFT.
 *
 * The endpoint returns one row per decision, newest-first. A single nightly re-sync rewrites the same
 * shift every run (delete the stored version, insert the freshly-derived one), so a fortnight of punches
 * arrives as dozens of near-identical rows — the same shift date repeated with "Created" and "Replaced"
 * scattered across the list in `created_at` order. Reading "what happened to this person's shifts" out
 * of that means joining rows by eye.
 *
 * So we group by the SHIFT the decision is about, fold each sync run into a single step, and derive the
 * one thing the reader is actually after: **is this shift in InHouse now, or not**. Nothing is dropped —
 * every raw decision stays reachable inside its group's timeline.
 */

/** A Luxon DateTime from an ISO string OR an already-parsed DateTime — else null. The axios response
 * interceptor (`convertIsoDates`) turns EVERY ISO date in a response into a Luxon DateTime, so by the
 * time values reach the UI `detail.start_time`, `created_at` etc. are DateTime objects, not strings.
 * Anything that reads them must accept both. */
export const toDateTime = (value: unknown): DateTime | null => {
	if (DateTime.isDateTime(value)) return value.isValid ? value : null;
	if (typeof value === "string") {
		const parsed = DateTime.fromISO(value);
		return parsed.isValid ? parsed : null;
	}
	return null;
};

/** What one sync run did to one shift. */
export type RunStepStatus = "created" | "replaced" | "removed" | "blocked" | "ignored" | "deferred";

/**
 * Where the shift stands after the LAST run that touched it — the conclusion the row leads with.
 * Deliberately three states, not six: the reader's question is "is it in, out, or did it never land".
 */
export type ShiftOutcome = "active" | "removed" | "notCreated";

export interface ShiftRunStep {
	syncRunId: string;
	/** When this run recorded its decisions about the shift (the latest of them). */
	at: DateTime | null;
	status: RunStepStatus;
	/** Every raw decision this run made about the shift, oldest first. */
	changes: LineageChange[];
	/**
	 * The one reason that explains `status` — the reason of the decision that produced it. A re-sync
	 * records two (the insert's "built from clock punches" AND the delete's "a newer punch replaced it");
	 * showing both next to a "Replaced" chip is redundant, so the row leads with the deciding one.
	 */
	primaryReason: string;
	/** Distinct reason slugs behind this step, in the order encountered. Full picture for the timeline. */
	reasons: string[];
	source: string;
	fileS3Key: string | null;
}

export interface ShiftLineageGroup {
	/** Stable grid row id — the shift window, or a synthetic key for decisions with no window. */
	key: string;
	/** Shift start, in the department timezone. Null when no decision recorded a window. */
	startAt: DateTime | null;
	/** Shift end, in the department timezone. Null when the decision recorded only a start. */
	endAt: DateTime | null;
	outcome: ShiftOutcome;
	/** One step per sync run that touched this shift, oldest first. */
	steps: ShiftRunStep[];
	/** The most recent step — what the sync last did, and the basis for `outcome`. */
	latestStep: ShiftRunStep;
	/** Raw decision count folded into this group (what the old table showed as separate rows). */
	changeCount: number;
	/** Decisions with no recorded shift window can't be attributed to a shift; surfaced, never dropped. */
	isUnattributed: boolean;
}

export interface ShiftLineageSummary {
	shifts: number;
	active: number;
	removed: number;
	notCreated: number;
	syncRuns: number;
	changes: number;
}

const UNATTRIBUTED_KEY = "unattributed";

/** Key for the shift a decision is about: its exact clock window, which is stable across re-syncs. */
const shiftKeyOf = (change: LineageChange): { key: string; start: DateTime | null; end: DateTime | null } => {
	const detail = change.detail ?? {};
	const start = toDateTime(detail.start_time ?? detail.start ?? detail.shift_start);
	const end = toDateTime(detail.end_time ?? detail.end ?? detail.shift_end);
	if (start) return { key: `${start.toMillis()}-${end ? end.toMillis() : "open"}`, start, end };

	// A decision about loose punches (nothing was written yet) carries no window — fall back to the
	// punch clock so those still land on the day they belong to instead of one "everything else" bucket.
	const punchAt = (change.punchIdentity ?? []).map((punch) => toDateTime(punch.event_time)).find((dt): dt is DateTime => dt !== null);
	if (punchAt) return { key: `punch-${punchAt.toMillis()}`, start: punchAt, end: null };

	return { key: UNATTRIBUTED_KEY, start: null, end: null };
};

/** What a single run did, from the mix of actions it recorded about one shift. */
const stepStatusOf = (changes: LineageChange[]): RunStepStatus => {
	const has = (action: string) => changes.some((change) => change.action === action);
	// Insert + delete in the SAME run is the re-sync signature: the stored version was dropped and an
	// equivalent one written back. That reads as "replaced", not as a removal — the shift is still there.
	if (has("insert")) return has("delete") ? "replaced" : "created";
	if (has("delete")) return "removed";
	if (has("skip")) return "blocked";
	// A deferred delete means the shift was KEPT (the sync declined to remove it at a window edge).
	if (has("defer")) return "deferred";
	return "ignored";
};

/** The action whose reason explains each status — so the row's reason text matches its chip. */
const DECIDING_ACTION: Record<RunStepStatus, SyncChangeAction> = {
	created: "insert",
	replaced: "delete",
	removed: "delete",
	blocked: "skip",
	ignored: "ignore",
	deferred: "defer",
};

const OUTCOME_BY_STATUS: Record<RunStepStatus, ShiftOutcome> = {
	created: "active",
	replaced: "active",
	deferred: "active",
	removed: "removed",
	blocked: "notCreated",
	ignored: "notCreated",
};

const changeTime = (change: LineageChange): number => toDateTime(change.created_at)?.toMillis() ?? 0;

/**
 * Groups a nurse's raw sync decisions into one entry per shift, newest shift first.
 *
 * Ordering is by SHIFT time, not by when the sync ran — the reader scans a person's calendar, and
 * `created_at` order shuffles shift dates arbitrarily whenever one run writes many shifts at once.
 */
export const groupEntriesByShift = (entries: NurseShiftLineageEntry[]): ShiftLineageGroup[] => {
	const buckets = new Map<string, { start: DateTime | null; end: DateTime | null; entries: NurseShiftLineageEntry[] }>();

	for (const entry of entries) {
		const { key, start, end } = shiftKeyOf(entry.change);
		const bucket = buckets.get(key);
		if (bucket) bucket.entries.push(entry);
		else buckets.set(key, { start, end, entries: [entry] });
	}

	const groups: ShiftLineageGroup[] = [];

	for (const [key, bucket] of buckets) {
		const byRun = new Map<string, NurseShiftLineageEntry[]>();
		for (const entry of bucket.entries) {
			const runEntries = byRun.get(entry.change.sync_run_id);
			if (runEntries) runEntries.push(entry);
			else byRun.set(entry.change.sync_run_id, [entry]);
		}

		const steps: ShiftRunStep[] = [...byRun.entries()]
			.map(([syncRunId, runEntries]) => {
				const changes = runEntries.map((entry) => entry.change).sort((a, b) => changeTime(a) - changeTime(b));
				const status = stepStatusOf(changes);
				const deciding = changes.find((change) => change.action === DECIDING_ACTION[status]) ?? changes[0];
				return {
					syncRunId,
					at: toDateTime(changes[changes.length - 1]?.created_at),
					status,
					changes,
					primaryReason: deciding.reason,
					reasons: [...new Set(changes.map((change) => change.reason))],
					source: runEntries[0].source,
					fileS3Key: runEntries[0].fileS3Key,
				};
			})
			.sort((a, b) => (a.at?.toMillis() ?? 0) - (b.at?.toMillis() ?? 0));

		const latestStep = steps[steps.length - 1];
		groups.push({
			key,
			startAt: bucket.start,
			endAt: bucket.end,
			outcome: OUTCOME_BY_STATUS[latestStep.status],
			steps,
			latestStep,
			changeCount: bucket.entries.length,
			isUnattributed: key === UNATTRIBUTED_KEY,
		});
	}

	// Newest shift first; decisions with no window sink to the bottom, where they read as a footnote.
	return groups.sort((a, b) => {
		if (a.isUnattributed !== b.isUnattributed) return a.isUnattributed ? 1 : -1;
		return (b.startAt?.toMillis() ?? 0) - (a.startAt?.toMillis() ?? 0);
	});
};

/** Roll-up for the strip above the table: the answer before the reader opens a single row. */
export const summarizeShiftGroups = (groups: ShiftLineageGroup[]): ShiftLineageSummary => ({
	shifts: groups.filter((group) => !group.isUnattributed).length,
	active: groups.filter((group) => group.outcome === "active").length,
	removed: groups.filter((group) => group.outcome === "removed").length,
	notCreated: groups.filter((group) => group.outcome === "notCreated").length,
	syncRuns: new Set(groups.flatMap((group) => group.steps.map((step) => step.syncRunId))).size,
	changes: groups.reduce((total, group) => total + group.changeCount, 0),
});
